import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { addYears } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { getBuildingOptions } from '../Steps/data/adminManager.data'
import {
  getClients,
  selectClients,
  selectStatusIsLoadedAll,
  setLoadingState,
} from '../../../../../../reducers/clientsReducer'
import {
  selectBuildings,
  fetchSingleBuilding,
} from '../../../../../../reducers/buildingSelectorReducer'
import { selectUserData } from '../../../../../../reducers/userDataReducer'
import { createGiftCard } from '../../../../../../reducers/rewardsReducer'
import {
  mapClientToUser,
  getRewardLinkCashValueOptions,
  rewardLinkCashValueCustom,
  setMinMaxValue,
} from '../helpers'
import {
  REWARDS_AVAILABLE_FLAG,
  getCheckboxHandler,
  getNamedHandler,
  getGiftCardCategoryOptions,
} from '../../../helpers'
import BaseView from '../BaseView/BaseView'
import NonResidentView from '../NonResidentView/NonResidentView'
import RewardsExternalSelectRecipient from '../Steps/RewardsExternalSelectRecipient'
import RewardsLinkDetails from '../Steps/RewardsLinkDetails'
import RewardsLinkSummary from '../Steps/RewardsLinkSummary'
import SelectField from '../../../../../FormLegacy/FormFieldsV2/SelectField'
import CheckboxField from '../../../../../FormLegacy/FormFieldsV2/CheckboxField'
import TextAreaField from '../../../../../FormLegacy/FormFieldsV2/TextAreaField'
import TextField from '../../../../../FormLegacy/FormFieldsV2/TextField'
import SelectDropdownInput from '../../../../../SelectDropdownInput/SelectDropdownInput'
import { TextInput } from '@/components/ui/text-input'
import { useSnackbar } from '../../../../../../helpers/hooks/useSnackbar'

const RewardLink = ({
  goBack = () => {},
  closePopup = () => {},
  userData,
  getClients,
  users,
  allClientsLoaded,
  selectedBuilding,
  buildings,
  createGiftCard,
  setLoadingState,
  fetchSingleBuilding,
  // Note: pre-selects and locks the recipient - used when sending a gift from a resident's own
  // profile, where the resident is implied by context and must not be changed. Locking the
  // resident also implies locking the building (no separate `lockBuilding` prop needed) - the
  // caller already scopes `selectedBuilding` to the resident's building in that case. It also
  // suppresses the post-send redirect to the standalone Gift Cards page below (see onSubmit) -
  // that redirect would otherwise navigate a Resident Profile user away from the profile - in
  // favor of calling `onSuccess`, which the caller uses to refetch its own already-visible table.
  lockResident = undefined,
  onSuccess = () => {},
}) => {
  const { t } = useTranslation(['rewards-gift', 'rewards-table', 'rewards-loyalty'])
  const navigate = useNavigate()
  const [clients, setClients] = useState(users)
  const [formState, setFormState] = useState(() => ({
    building: selectedBuilding ? selectedBuilding.id : null,
    resident: lockResident ?? null,
    nonResident: false,
    recipientName: null,
    recipientEmail: null,
    card: null,
    message: null,
    cardValue: null,
    customCardValue: null,
    expDate: addYears(Date.now(), 1),
    note: null,
    sendSms: false,
    nonResidentSmsNumber: '',
  }))
  const [isValidEmail, setIsValidEmail] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [clearInput, setClearInput] = useState(() => () => {})
  const { displaySnackbar } = useSnackbar()

  const isPropertyManager = userData.user.role === 'PropertyManager'

  const buildingData = isPropertyManager
    ? (userData.buildingIds || []).filter((building) => building[REWARDS_AVAILABLE_FLAG])
    : buildings || []

  const buildingOptions = getBuildingOptions(buildingData) || []

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleCheckboxChange = getCheckboxHandler(handleFieldChange)
  const handleSearchSelect = getNamedHandler(handleFieldChange)
  const handleBuildingChange = (e) => {
    handleSearchSelect(null, 'resident')
    setClients([])
    clearInput()
    handleFieldChange(e)
  }

  const fetchClients = async (query = '', isScrolling = false, buildingId = formState.building) => {
    const limit = 15
    const offset =
      clients.length % limit === 0
        ? clients.length / limit
        : (clients.length - (clients.length % limit)) / limit

    await getClients(isScrolling ? offset + 1 : 1, buildingId, undefined, query, limit)
  }

  useEffect(() => {
    setClients(users)
  }, [users])

  useEffect(() => {
    if (formState.card) {
      setFormState({
        ...formState,
        cardValue: null,
      })
    }
  }, [formState.card])

  const onSubmitFinally = async () => {
    if (selectedBuilding.id) {
      await fetchSingleBuilding(selectedBuilding.id)
    }
    setIsLoading(false)
  }

  const onSubmit = async () => {
    const requestData = {
      amount: parseInt(formState.customCardValue ?? formState.cardValue, 10),
      buildingId: formState.building,
      internalNote: formState.note,
      message: formState.message,
      recipientName: formState.nonResident ? formState.recipientName : formState.resident.label,
      recipientEmail: formState.nonResident ? formState.recipientEmail : formState.resident.email,
      category: formState.category,
      sendSms: formState.sendSms,
      ...(formState.nonResident &&
        formState.recipientPhone && {
          recipientPhone: '+1' + formState.recipientPhone,
        }),
    }

    if (!formState.nonResident) {
      requestData.clientId = formState.resident.value
    } else {
      requestData.isNonResident = true
    }

    setIsLoading(true)
    createGiftCard(requestData, 'link')
      .then(() => {
        displaySnackbar(
          t('rewards-gift:rewardLink.cashRewardLinkSentSuccess', {
            recipientLabel: formState.nonResident
              ? formState.recipientName
              : formState.resident.label,
          }),
        )
        closePopup()
        if (lockResident) {
          onSuccess()
        } else {
          navigate(`/dashboard/building-dashboard/rewards/gift-cards/${selectedBuilding.id}`)
        }
      })
      .catch(() => displaySnackbar(t('rewards-gift:rewardLink.paymentProcessingError'), true))
      .finally(onSubmitFinally)
  }

  const selectRecipientButtonEnabled =
    !!formState.building && formState.nonResident
      ? !!formState.recipientEmail &&
        !!formState.recipientName &&
        isValidEmail &&
        !!formState.category
      : !!formState.resident && !!formState.category

  const isCustomCardValueValid =
    formState.cardValue === rewardLinkCashValueCustom ? !!formState.customCardValue : true
  const settingsButtonEnabled =
    !!formState.cardValue && isCustomCardValueValid && !!formState.message

  const steps = [
    {
      Component: RewardsExternalSelectRecipient,
      title: t('rewards-gift:sendGiftSteps.selectRecipient'),
      config: {
        canGoNext: selectRecipientButtonEnabled,
        inputs: {
          // Note: locking the resident also locks the building (see `lockResident` note above)
          // and drops the "send to non-resident" option entirely - both would let the sender
          // pick a different recipient than the one this flow was opened for.
          ...(!lockResident && {
            first: {
              render: () => (
                <SelectField
                  name="building"
                  options={buildingOptions}
                  placeholder={t('rewards-gift:sendGiftForm.selectBuildingPlaceholder')}
                  onChange={handleBuildingChange}
                  value={formState.building}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('rewards-gift:sendGiftForm.selectBuildingLabel'),
            },
          }),
          second: {
            render: () =>
              lockResident ? (
                <TextInput disabled readOnly value={lockResident.label} />
              ) : (
                <SelectDropdownInput
                  placeholder={t('rewards-gift:sendGiftForm.clickAndTypeSearchResidentPlaceholder')}
                  data={users}
                  onSelectOption={(item) => handleSearchSelect(item, 'resident')}
                  fetchData={(skip, query, isScrolling, buildingId) =>
                    fetchClients(query, isScrolling, buildingId)
                  }
                  isLoadedAll={allClientsLoaded}
                  isDisabled={formState.nonResident}
                  setLoadingState={setLoadingState}
                  dataToRefetch={formState.building}
                  showSelectedInDropdown
                  clearOnDemand={setClearInput}
                />
              ),
            label: t('rewards-gift:sendGiftForm.selectResidentLabel'),
            disabled: formState.nonResident,
          },
          ...(!lockResident && {
            third: {
              render: () => (
                <CheckboxField
                  name="nonResident"
                  formState={formState}
                  onChange={(e) => {
                    handleCheckboxChange(e)
                    setFormState((prevState) => ({ ...prevState, recipientPhone: '' }))
                  }}
                />
              ),
              renderExtra: () =>
                formState.nonResident && (
                  <NonResidentView
                    handleFieldChange={handleFieldChange}
                    setIsValidEmail={setIsValidEmail}
                    formState={formState}
                    isValidEmail={isValidEmail}
                  />
                ),
              label: t('rewards-gift:sendGiftForm.sendCardToNonResidentLabel'),
            },
          }),
          fourth: {
            render: () => (
              <SelectField
                name="category"
                options={getGiftCardCategoryOptions(t)}
                placeholder={t('rewards-gift:sendGiftForm.selectCardCategoryPlaceholder')}
                onChange={handleFieldChange}
                value={formState.category}
                selectStyle={{ width: '100%' }}
              />
            ),
            label: t('rewards-table:giftCardCategoryPlaceholder'),
          },
        },
      },
    },
    {
      Component: RewardsLinkDetails,
      title: t('rewards-gift:sendGiftSteps.settings'),
      config: {
        canGoNext: settingsButtonEnabled,
        inputs: {
          first: {
            render: () => (
              <SelectField
                name="cardValue"
                options={getRewardLinkCashValueOptions(t)}
                placeholder={t('rewards-gift:rewardLink.selectCashRewardValuePlaceholder')}
                onChange={(e) => {
                  setFormState((prev) => ({ ...prev, customCardValue: null }))
                  handleFieldChange(e)
                }}
                value={formState.cardValue}
                selectStyle={{ width: '100%' }}
              />
            ),
            label: t('rewards-gift:rewardLink.selectCashRewardValueLabel'),
          },
          ...(formState.cardValue === rewardLinkCashValueCustom && {
            second: {
              render: () => (
                <React.Fragment>
                  <TextField
                    name="customCardValue"
                    onChange={handleFieldChange}
                    onBlur={(e) => {
                      handleFieldChange(setMinMaxValue(e, parseInt(1, 10), parseInt(10000, 10)))
                    }}
                    value={formState.customCardValue}
                    placeholder={t('rewards-gift:sendGiftForm.enterCardValuePlaceholder')}
                    style={{ width: '100%' }}
                  />
                  <span className="input-desc-label">
                    {t('rewards-gift:rewardLink.enterWholeDollarAmountInfo')}
                  </span>
                </React.Fragment>
              ),
              label: <div>{t('rewards-gift:rewardLink.enterAmountLabel')}</div>,
            },
          }),
          third: {
            render: () => (
              <React.Fragment>
                <TextField
                  name="note"
                  value={formState.note}
                  placeholder={t('rewards-gift:sendGiftForm.enterInternalNotePlaceholder')}
                  style={{ width: '100%' }}
                  onChange={handleFieldChange}
                />
                <span className="input-desc-label">
                  {t('rewards-gift:sendGiftForm.internalNoteDescription')}
                </span>
              </React.Fragment>
            ),
            label: t('rewards-gift:sendGiftForm.internalNoteLabel'),
          },
          forth: {
            render: () => (
              <TextAreaField
                name="message"
                onChange={handleFieldChange}
                value={formState.message}
                placeholder={t('rewards-gift:sendGiftForm.messagePlaceholder')}
                style={{ width: '100%' }}
              />
            ),
            label: t('rewards-gift:sendGiftForm.messageToRecipientLabel'),
          },
        },
      },
    },
    {
      Component: RewardsLinkSummary,
      title: t('rewards-gift:sendGiftSteps.summary'),
      config: {
        formState,
        onSubmit,
        currentBalance: selectedBuilding.rewardsBalance,
        isLoading,
        setFormState,
      },
    },
  ]

  return <BaseView steps={steps} goBack={goBack} />
}

RewardLink.propTypes = {
  goBack: PropTypes.func,
  userData: PropTypes.object,
  getClients: PropTypes.func,
  users: PropTypes.array,
  allClientsLoaded: PropTypes.bool,
  selectedBuilding: PropTypes.object,
  buildings: PropTypes.array,
  closePopup: PropTypes.func,
  createGiftCard: PropTypes.func,
  setLoadingState: PropTypes.func,
  fetchSingleBuilding: PropTypes.func,
  lockResident: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    email: PropTypes.string,
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onSuccess: PropTypes.func,
}

export default connect(
  (state) => ({
    userData: selectUserData(state),
    buildings: selectBuildings(state),
    users: selectClients(state).map(mapClientToUser),
    allClientsLoaded: selectStatusIsLoadedAll(state),
  }),
  {
    getClients,
    createGiftCard,
    setLoadingState,
    fetchSingleBuilding,
  },
)(RewardLink)
